/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import {
  Modal, Form, Button, Spinner,
} from 'react-bootstrap';
import VoucherService from '../../../Services/VoucherService';
import { formatBookingDatetime, DEFAULT_TIMEZONE } from '../../../Utils/timezone';

// Language options for PDF
const LANGUAGES = [
  { value: 'en', label: 'English', flag: '🇬🇧' },
  { value: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { value: 'hu', label: 'Magyar', flag: '🇭🇺' },
  { value: 'ru', label: 'Русский', flag: '🇷🇺' },
  { value: 'uk', label: 'Українська', flag: '🇺🇦' },
];

export default function TripConfirmationModal({ order, show, handleClose }) {
  const [language, setLanguage] = useState('en');
  const [combinePrices, setCombinePrices] = useState(false);
  const [hidePrice, setHidePrice] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const hasLinkedBookings = order.linked_booking_ids?.length > 0;

  // Reset state when modal opens
  useEffect(() => {
    if (show) {
      setLanguage('en');
      setCombinePrices(false);
      setHidePrice(false);
      setGeneratingPdf(false);
    }
  }, [show]);

  // eslint-disable-next-line no-underscore-dangle
  const handleDownloadPdf = async () => {
    setGeneratingPdf(true);
    try {
      await VoucherService.downloadPdf(
        // eslint-disable-next-line no-underscore-dangle
        order._id,
        language,
        {
          linkedBookingIds: combinePrices ? order.linked_booking_ids : [],
          combinePrices,
          hidePrice,
          isPaid: order.payment?.paid === true,
        },
      );
      handleClose();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('PDF generation failed:', err);
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Format date for display using booking's timezone
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const tz = order.timezone || DEFAULT_TIMEZONE;
    return formatBookingDatetime(dateStr, tz, 'ddd, DD MMM YYYY, HH:mm');
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa fa-file-pdf me-2 text-primary" />
          Download Voucher
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Booking Summary */}
        <div
          style={{
            background: '#f8f9fa',
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '20px',
            borderLeft: '4px solid #eba23a',
          }}
        >
          <div style={{ marginBottom: '8px' }}>
            <span style={{ color: '#6c757d', fontSize: '0.85rem' }}>
              Booking Reference:
            </span>
            {' '}
            <strong style={{ color: '#eba23a', fontSize: '1.1rem' }}>
              #
              {order.booking_reference}
            </strong>
          </div>
          <div style={{ marginBottom: '8px' }}>
            <span style={{ color: '#6c757d', fontSize: '0.85rem' }}>
              Customer:
            </span>
            {' '}
            <strong>{order.full_name}</strong>
          </div>
          <div>
            <span style={{ color: '#6c757d', fontSize: '0.85rem' }}>
              Date:
            </span>
            {' '}
            <strong>{formatDate(order.date_datetime)}</strong>
          </div>
        </div>

        {/* Language Selection */}
        <Form.Group className="mb-3">
          <Form.Label>
            PDF Language
          </Form.Label>
          <Form.Select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          >
            {LANGUAGES.map((lang) => (
              <option key={lang.value} value={lang.value}>
                {lang.flag}
                {' '}
                {lang.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {/* Combine Prices Toggle */}
        {hasLinkedBookings && (
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              id="combinePrices"
              label="Combine linked bookings"
              checked={combinePrices}
              onChange={(e) => setCombinePrices(e.target.checked)}
            />
            <Form.Text className="text-muted">
              {order.linked_booking_ids.length}
              {' '}
              linked booking(s)
            </Form.Text>
          </Form.Group>
        )}

        {/* Hide Price Toggle */}
        <Form.Group className="mb-3">
          <Form.Check
            type="checkbox"
            id="hidePrice"
            label="Hide price on voucher"
            checked={hidePrice}
            onChange={(e) => setHidePrice(e.target.checked)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={generatingPdf}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleDownloadPdf}
          disabled={generatingPdf}
        >
          {generatingPdf ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Generating...
            </>
          ) : (
            <>
              <i className="fa fa-download me-2" />
              Download PDF
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
